<div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
  <div class="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 w-full max-w-2xl">

    <!-- Brand -->
    <div class="text-center mb-8">
      <div class="w-14 h-14 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4">
        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
        </svg>
      </div>
      <h1 class="text-2xl font-bold text-gray-900">Accept Invitation</h1>
      <p class="text-gray-500 text-sm mt-1">Set up your account to get started</p>
    </div>

    <!-- Loading invitation -->
    @if (isLoadingInvitation) {
      <div class="flex flex-col items-center justify-center py-12 gap-3">
        <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-gray-500 text-sm">Loading invitation...</p>
      </div>
    }

    <!-- Error (invalid/expired code with no form) -->
    @if (!isLoadingInvitation && errorMessage && !invitedEmail && !invitedPhone) {
      <div class="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <svg class="h-8 w-8 text-red-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <p class="text-red-700 font-medium">{{ errorMessage }}</p>
        <a routerLink="/login" class="mt-3 inline-block text-sm text-blue-600 hover:underline">Back to login</a>
      </div>
    }

    <!-- Form (shown once invitation is loaded) -->
    @if (!isLoadingInvitation && (invitedEmail || invitedPhone)) {

      <!-- Invitation info banner -->
      <div class="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <p class="text-sm font-medium text-blue-900 mb-1">You've been invited</p>
        @if (invitedEmail) {
          <p class="text-xs text-blue-700">Email: {{ invitedEmail }}</p>
        }
        @if (invitedPhone) {
          <p class="text-xs text-blue-700">Phone: {{ invitedPhone }}</p>
        }
      </div>

      <form [formGroup]="form" (ngSubmit)="onSubmit()" class="space-y-5">

        <!-- Code (hidden when pre-filled) -->
        <input type="hidden" formControlName="invitationCode" />

        <!-- Full Name + Username -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="fullName" class="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
            <input
              id="fullName"
              type="text"
              formControlName="fullName"
              placeholder="Enter your full name"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
              [class.border-red-400]="f['fullName'].invalid && f['fullName'].touched"
            />
            @if (f['fullName'].invalid && f['fullName'].touched) {
              <p class="text-red-500 text-xs mt-1">Full name is required</p>
            }
          </div>

          <div>
            <label for="username" class="block text-sm font-medium text-gray-700 mb-2">Username *</label>
            <input
              id="username"
              type="text"
              formControlName="username"
              placeholder="Choose a username"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
              [class.border-red-400]="f['username'].invalid && f['username'].touched"
            />
            @if (f['username'].invalid && f['username'].touched) {
              <p class="text-red-500 text-xs mt-1">Username is required (min 3 characters)</p>
            }
          </div>
        </div>

        <!-- Phone (required) + Email (optional) -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="accountPhone" class="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
            <input
              id="accountPhone"
              type="tel"
              formControlName="accountPhone"
              placeholder="+94771234567"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
              [class.border-red-400]="f['accountPhone'].invalid && f['accountPhone'].touched"
            />
            @if (f['accountPhone'].invalid && f['accountPhone'].touched) {
              <p class="text-red-500 text-xs mt-1">Phone number is required</p>
            }
          </div>

          <div>
            <label for="accountEmail" class="block text-sm font-medium text-gray-700 mb-2">Email <span class="text-gray-400 font-normal">(optional)</span></label>
            <input
              id="accountEmail"
              type="email"
              formControlName="accountEmail"
              placeholder="your@email.com"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
            />
          </div>
        </div>

        <!-- Password + Confirm -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 mb-2">Password *</label>
            <input
              id="password"
              type="password"
              formControlName="password"
              placeholder="Choose a password"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
              [class.border-red-400]="f['password'].invalid && f['password'].touched"
            />
            @if (f['password'].invalid && f['password'].touched) {
              <p class="text-red-500 text-xs mt-1">Password is required (min 6 characters)</p>
            }
          </div>

          <div>
            <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
            <input
              id="confirmPassword"
              type="password"
              formControlName="confirmPassword"
              placeholder="Confirm your password"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
              [class.border-red-400]="passwordMismatch"
            />
            @if (passwordMismatch) {
              <p class="text-red-500 text-xs mt-1">Passwords do not match</p>
            }
          </div>
        </div>

        <!-- Submit error -->
        @if (errorMessage) {
          <div class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="flex items-center gap-2">
              <svg class="h-5 w-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <p class="text-sm text-red-700">{{ errorMessage }}</p>
            </div>
          </div>
        }

        <!-- Submit -->
        <button
          type="submit"
          [disabled]="isLoading"
          class="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          @if (isLoading) {
            <span class="flex items-center justify-center gap-2">
              <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Setting up your account...
            </span>
          } @else {
            <span class="flex items-center justify-center gap-2">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              Create My Account
            </span>
          }
        </button>
      </form>
    }

    <div class="mt-6 text-center">
      <p class="text-sm text-gray-500">
        Already have an account?
        <a routerLink="/login" class="text-blue-600 hover:underline font-medium">Sign in here</a>
      </p>
    </div>

  </div>
</div>
